import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { intersection, includes } from 'lodash';
import { FormHelpText, Type, TypeList, TypeListItem } from '@spotify-internal/encore-creator-web';
import colors from '@spotify-internal/encore-foundation';
import styled from 'styled-components';
import { PODCAST_SUPPORT_EMAIL } from 'lib/constants';
import { FormattedMessage } from 'react-intl';

const MailLink = styled.a`
  color: ${colors.azure};
  font-weight: 600;
  text-decoration: underline;
  &:hover {
    color: black;
  }
`;

const GENERAL_ERRORS = {
  DOWNLOAD_FAILURE: 'DOWNLOAD_FAILURE',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  NEEDS_APPROVAL: 'NEEDS_APPROVAL',
  INVALID_XML: 'INVALID_XML',
  VIDEO_PODCAST: 'VIDEO_PODCAST',
  DIFFERENT_SHOW: 'DIFFERENT_SHOW',
};

const MISSING_ERRORS = {
  MISSING_IMAGE: 'MISSING_IMAGE',
  MISSING_DESCRIPTION: 'MISSING_DESCRIPTION',
  MISSING_TITLE: 'MISSING_TITLE',
  MISSING_EPISODES: 'MISSING_EPISODES',
  MISSING_AUTHOR: 'MISSING_AUTHOR',
  MISSING_GUID: 'MISSING_GUID',
  MISSING_EMAIL: 'MISSING_EMAIL',
};

const errorMessages = {
  [GENERAL_ERRORS.DOWNLOAD_FAILURE]: (
    <FormattedMessage
      id="podcast-update.update-url-error-download-failure"
      defaultMessage="That URL doesn’t look right. Make sure you typed it correctly."
    />
  ),
  [GENERAL_ERRORS.UNKNOWN_ERROR]: (
    <FormattedMessage
      id="podcast-update.update-url-error-unknown"
      defaultMessage="Something has gone wrong. Please try again"
    />
  ),
  [GENERAL_ERRORS.NEEDS_APPROVAL]: (
    <FormattedMessage
      id="podcast-update.update-url-error-needs-approval"
      defaultMessage="You’re a VIP! To get this podcast on Spotify, you need to <link>contact our team</link> for help."
      values={{
        link: (...chunks) => <MailLink href={`mailto:${PODCAST_SUPPORT_EMAIL}`}>{chunks}</MailLink>,
      }}
    />
  ),
  [GENERAL_ERRORS.INVALID_XML]: (
    <FormattedMessage
      id="podcast-update.update-url-error-invalid-xml"
      defaultMessage="Your RSS feed failed to load. Make sure you added the right link."
    />
  ),
  [GENERAL_ERRORS.VIDEO_PODCAST]: (
    <FormattedMessage
      id="podcast-update.update-url-error-videos"
      defaultMessage="We’re unable to accept podcasts with videos. <a>Learn more</a>"
      values={{
        a: (...chunks) => <a href="/faq#can-i-have-video-episodes-in-my-rss-feed">{chunks}</a>,
      }}
    />
  ),
  [GENERAL_ERRORS.DIFFERENT_SHOW]: (
    <FormattedMessage
      id="podcast-update.update-url-error-different-show"
      defaultMessage="The link to the RSS feed is for a different podcast."
    />
  ),
  [MISSING_ERRORS.MISSING_IMAGE]: (
    <FormattedMessage
      id="podcast-update.update-url-error-missing-image"
      defaultMessage="Cover art"
    />
  ),
  [MISSING_ERRORS.MISSING_DESCRIPTION]: (
    <FormattedMessage
      id="podcast-update.update-url-error-description"
      defaultMessage="Description"
    />
  ),
  [MISSING_ERRORS.MISSING_TITLE]: (
    <FormattedMessage id="podcast-update.update-url-error-title" defaultMessage="Title" />
  ),
  [MISSING_ERRORS.MISSING_EPISODES]: (
    <FormattedMessage
      id="podcast-update.update-url-error-missing-episodes"
      defaultMessage="Episodes"
    />
  ),
  [MISSING_ERRORS.MISSING_AUTHOR]: (
    <FormattedMessage id="podcast-update.update-url-error-missing-author" defaultMessage="Author" />
  ),
  [MISSING_ERRORS.MISSING_EMAIL]: (
    <FormattedMessage
      id="podcast-update.update-url-error-email-address"
      defaultMessage="Email address"
    />
  ),
  [MISSING_ERRORS.MISSING_GUID]: (
    <FormattedMessage
      id="podcast-update.update-url-error-guid"
      defaultMessage="Your podcast needs a globally unique identifier (GUID).
      <a>Learn more</a>"
      values={{
        a: (...chunks) => <a href="/faq#whats-a-globally-unique-identifier-guid">{chunks}</a>,
      }}
    />
  ),
};

const ErrorContainer = styled.div`
  margin-top: 16px;
`;

const StyledTypeListItem = styled(TypeListItem)`
  font-size: 14px;
`;

export class PodcastUpdateUrlErrorFeedback extends PureComponent {
  static propTypes = {
    errors: PropTypes.arrayOf(PropTypes.string),
  };

  isGeneralError = () => {
    return intersection(this.props.errors, Object.values(GENERAL_ERRORS)).length > 0;
  };

  generalErrorTemplate = errors => (
    <Fragment>
      {errors.map(error => (
        <Type
          as="p"
          data-qa="download-failure"
          color={error === GENERAL_ERRORS.NEEDS_APPROVAL ? 'azure' : 'failure'}
          variant={Type.sm}
          key={error}
        >
          {errorMessages[GENERAL_ERRORS[error]]}
        </Type>
      ))}
      {includes(errors, GENERAL_ERRORS.INVALID_XML) && (
        <FormHelpText>
          <FormattedMessage
            id="podcast-update.update-url-error-general-footer"
            defaultMessage="Need help? Try looking in the Help section of your podcast aggregator/host."
          />
        </FormHelpText>
      )}
    </Fragment>
  );

  missingInfoErrorTemplate = errors => (
    <Fragment>
      <FormHelpText error>
        <FormattedMessage
          id="podcast-update.update-url-error-missing-info"
          defaultMessage="Your podcast RSS feed is missing some things:"
        />
        <TypeList.ul data-qa="error-list">
          {errors.map(errorType => (
            <StyledTypeListItem condensed key={errorType}>
              {errorMessages[errorType]}
            </StyledTypeListItem>
          ))}
        </TypeList.ul>
      </FormHelpText>
      <FormHelpText>
        <FormattedMessage
          id="podcast-update.update-url-error-missing-footer"
          defaultMessage="Need help? Try looking in the Help section of your podcast aggregator/host."
        />
      </FormHelpText>
    </Fragment>
  );

  render() {
    const errorMessage = this.isGeneralError()
      ? this.generalErrorTemplate(this.props.errors)
      : this.missingInfoErrorTemplate(this.props.errors);
    return <ErrorContainer>{errorMessage}</ErrorContainer>;
  }
}
